import { env } from '~/common/api/env';

import { PromptTypesEnum } from '../enums/prompt-types-enum';
import defaultImage from '../../../../public/images/agent-image.jpeg';

export type TPromptMediaValue = string | string[] | null | undefined;

export interface IPromptMediaItem {
  originalUrl: string;
  displayUrl: string;
  isVideo: boolean;
  posterUrl?: string;
}

const normalizeSinglePromptMediaUrl = (mediaUrl: string): string =>
  mediaUrl.startsWith('http') || mediaUrl.startsWith('data:')
    ? mediaUrl
    : `${env.NEXT_PUBLIC_API_URL}${mediaUrl}`;

export const toPromptMediaArray = (
  mediaUrl: TPromptMediaValue
): string[] => {
  if (Array.isArray(mediaUrl)) {
    return mediaUrl.filter(Boolean);
  }

  if (!mediaUrl) {
    return [];
  }

  return [mediaUrl];
};

export const normalizePromptMediaUrl = (
  mediaUrl: TPromptMediaValue,
  type?: PromptTypesEnum,
  fallbackToDefault = false
): string[] => {
  const normalized = toPromptMediaArray(mediaUrl).map(
    normalizeSinglePromptMediaUrl
  );

  if (!normalized.length && fallbackToDefault) {
    return [defaultImage.src];
  }

  return normalized;
};

export const normalizePromptMediaFields = <
  T extends {
    mediaUrl?: TPromptMediaValue;
    compactMediaUrl?: TPromptMediaValue;
    type?: PromptTypesEnum;
  },
>(
  prompt: T,
  fallbackToDefault = false
): Omit<T, 'mediaUrl' | 'compactMediaUrl'> & {
  mediaUrl: string[];
  compactMediaUrl: string[];
} => ({
  ...prompt,
  mediaUrl: normalizePromptMediaUrl(
    prompt.mediaUrl,
    prompt.type,
    fallbackToDefault
  ),
  compactMediaUrl: normalizePromptMediaUrl(prompt.compactMediaUrl, prompt.type),
});

export const isVideoMediaUrl = (mediaUrl?: string | null): boolean => {
  if (!mediaUrl) {
    return false;
  }

  if (mediaUrl.startsWith('data:video/')) {
    return true;
  }

  if (mediaUrl.startsWith('data:image/')) {
    return false;
  }

  const sanitizedMediaUrl = mediaUrl.split('?')[0].toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/i.test(sanitizedMediaUrl);
};

export const resolvePromptMediaItems = (
  mediaUrls: TPromptMediaValue,
  compactMediaUrls?: TPromptMediaValue,
  type?: PromptTypesEnum,
  fallbackToDefault = false
): IPromptMediaItem[] => {
  const normalizedMediaUrls = normalizePromptMediaUrl(
    mediaUrls,
    type,
    fallbackToDefault
  );
  const normalizedCompactMediaUrls = normalizePromptMediaUrl(
    compactMediaUrls,
    type
  );

  return normalizedMediaUrls.map((mediaUrl, index) => {
    const isVideo = isVideoMediaUrl(mediaUrl);
    const compactMediaUrl = normalizedCompactMediaUrls[index];
    const hasPosterUrl =
      !!compactMediaUrl &&
      compactMediaUrl !== mediaUrl &&
      !isVideoMediaUrl(compactMediaUrl);

    return {
      originalUrl: mediaUrl,
      displayUrl: isVideo
        ? mediaUrl
        : normalizedCompactMediaUrls[index] || mediaUrl,
      isVideo,
      posterUrl: hasPosterUrl ? compactMediaUrl : undefined,
    };
  });
};
